import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount, CustomSelect, TableSkeleton, btnSecondary } from '@redpanda/ui';
import { toIntlLocale, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { ReportLine, useBalanceSheet, useIncomeStatement, useTrialBalance, yearOptions } from '../stores/reportStore';
import { useDataChanged } from '../utils/dataEvents';

const TABS = ['income-statement', 'balance-sheet', 'trial-balance'] as const;
type Tab = typeof TABS[number];
const TAB_KEYS: Record<Tab, string> = { 'income-statement': 'incomeStatement', 'balance-sheet': 'balanceSheet', 'trial-balance': 'trialBalance' };

/** 報表用的一列：代號＋名稱＋金額（靠右、tabular-nums） */
const Row: React.FC<{ line: ReportLine }> = ({ line }) => (
  <tr>
    <td className="py-2 pl-5 pr-3 w-24 tabular-nums text-fg-secondary">{line.code}</td>
    <td className="py-2 pr-3 text-fg">{line.name}</td>
    <td className="py-2 pl-3 pr-5 w-44 text-right"><Amount value={line.amount} /></td>
  </tr>
);

/** 區段標題列、合計列 */
const SectionHead: React.FC<{ title: string }> = ({ title }) => (
  <tr className="bg-gray-50 dark:bg-slate-800/60">
    <td colSpan={3} className="py-2 px-5 text-[13px] font-semibold uppercase tracking-wider text-fg-secondary">{title}</td>
  </tr>
);
const TotalRow: React.FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
  <tr className={strong ? 'border-t-2 border-line' : 'border-t border-line'}>
    <td />
    <td className={`py-2.5 pr-3 ${strong ? 'font-semibold text-fg' : 'font-medium text-fg'}`}>{label}</td>
    <td className={`py-2.5 pl-3 pr-5 text-right ${strong ? 'font-semibold' : 'font-medium'}`}><Amount value={value} /></td>
  </tr>
);

/**
 * 財務報表：損益表（該年度）、資產負債表與試算表（累計到年底）
 * 只算已過帳分錄；依年度快取（先顯示上次資料、背景更新）；可列印（§3-16）
 */
const ReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext)!;
  const locale = toIntlLocale(i18n.language);
  const [tab, setTab] = useRememberedState<Tab>('accounting-ui:reports.tab', 'income-statement', TABS);
  const [year, setYear] = useState(new Date().getFullYear());

  const income = useIncomeStatement(tab === 'income-statement' ? { year } : null);
  const balance = useBalanceSheet(tab === 'balance-sheet' ? { year } : null);
  const trial = useTrialBalance(tab === 'trial-balance' ? { year } : null);
  const current = { 'income-statement': income, 'balance-sheet': balance, 'trial-balance': trial }[tab];

  useEffect(() => {
    current.refetch();
  }, [tab, year]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnFocus(current.refetch);
  useDataChanged(['journals', 'accounts', 'periods'], current.refetch);

  const reportTitle = t(`reports.tabs.${TAB_KEYS[tab]}`);
  const periodText = tab === 'income-statement'
    ? t('reports.periodYear', { year })
    : t('reports.asOf', { date: new Date(Date.UTC(year, 11, 31)).toLocaleDateString(locale, { timeZone: 'UTC', dateStyle: 'long' }) });

  const empty = <p className="px-5 py-12 text-center text-sm text-fg-secondary">{t('reports.noData', { year })}</p>;
  const tableBox = 'bg-surface rounded-lg border border-line overflow-hidden';

  const renderBody = () => {
    if (!current.data) return <TableSkeleton rows={8} columns={3} showHeader={false} />;

    if (tab === 'income-statement' && income.data) {
      const d = income.data;
      if (d.income.length === 0 && d.expenses.length === 0) return <div className={tableBox}>{empty}</div>;
      return (
        <div className={tableBox}>
          <table className="w-full text-sm">
            <tbody>
              <SectionHead title={t('reports.incomeAccounts')} />
              {d.income.map(line => <Row key={line.id} line={line} />)}
              <TotalRow label={t('reports.totalIncome')} value={d.totalIncome} />
              <SectionHead title={t('reports.expenseAccounts')} />
              {d.expenses.map(line => <Row key={line.id} line={line} />)}
              <TotalRow label={t('reports.totalExpenses')} value={d.totalExpenses} />
              <TotalRow label={t('reports.netIncome')} value={d.netIncome} strong />
            </tbody>
          </table>
        </div>
      );
    }

    if (tab === 'balance-sheet' && balance.data) {
      const d = balance.data;
      if (d.assets.length === 0 && d.liabilities.length === 0 && d.equity.length === 0 && d.retainedEarnings === 0) {
        return <div className={tableBox}>{empty}</div>;
      }
      return (
        <>
          <div className={tableBox}>
            <table className="w-full text-sm">
              <tbody>
                <SectionHead title={t('reports.assetAccounts')} />
                {d.assets.map(line => <Row key={line.id} line={line} />)}
                <TotalRow label={t('reports.totalAssets')} value={d.totalAssets} strong />
                <SectionHead title={t('reports.liabilityAccounts')} />
                {d.liabilities.map(line => <Row key={line.id} line={line} />)}
                <TotalRow label={t('reports.totalLiabilities')} value={d.totalLiabilities} />
                <SectionHead title={t('reports.equityAccounts')} />
                {d.equity.map(line => <Row key={line.id} line={line} />)}
                <tr>
                  <td className="py-2 pl-5 pr-3 w-24" />
                  <td className="py-2 pr-3 text-fg">{t('reports.retainedEarnings')}</td>
                  <td className="py-2 pl-3 pr-5 w-44 text-right"><Amount value={d.retainedEarnings} /></td>
                </tr>
                <TotalRow label={t('reports.totalEquity')} value={d.totalEquity} />
                <TotalRow label={t('reports.liabilitiesPlusEquity')} value={d.totalLiabilities + d.totalEquity} strong />
              </tbody>
            </table>
          </div>
          {!d.balanced && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{t('reports.unbalanced')}</p>}
        </>
      );
    }

    if (tab === 'trial-balance' && trial.data) {
      const d = trial.data;
      if (d.rows.length === 0) return <div className={tableBox}>{empty}</div>;
      return (
        <>
          <div className={tableBox}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="bg-gray-50 dark:bg-slate-800/60">
                  <tr className="text-[13px] font-semibold uppercase tracking-wider text-fg-secondary">
                    <th className="py-2 pl-5 pr-3 w-24 text-left font-semibold">{t('reports.columns.accountCode')}</th>
                    <th className="py-2 pr-3 text-left font-semibold">{t('reports.columns.accountName')}</th>
                    <th className="py-2 px-3 w-40 text-right font-semibold">{t('reports.columns.debit')}</th>
                    <th className="py-2 pl-3 pr-5 w-40 text-right font-semibold">{t('reports.columns.credit')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {d.rows.map(row => (
                    <tr key={row.id}>
                      <td className="py-2 pl-5 pr-3 tabular-nums text-fg-secondary">{row.code}</td>
                      <td className="py-2 pr-3 text-fg">{row.name}</td>
                      <td className="py-2 px-3 text-right">{row.debit ? <Amount value={row.debit} /> : null}</td>
                      <td className="py-2 pl-3 pr-5 text-right">{row.credit ? <Amount value={row.credit} /> : null}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-line font-semibold text-fg">
                    <td />
                    <td className="py-2.5 pr-3">{t('reports.total')}</td>
                    <td className="py-2.5 px-3 text-right"><Amount value={d.totalDebit} /></td>
                    <td className="py-2.5 pl-3 pr-5 text-right"><Amount value={d.totalCredit} /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {!d.balanced && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{t('reports.trialUnbalanced')}</p>}
        </>
      );
    }
    return null;
  };

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4 no-print">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('reports.title')}</h1>
          <div className="flex items-center space-x-3">
            <div className="w-28">
              <CustomSelect
                value={String(year)}
                onChange={value => setYear(Number(value))}
                options={yearOptions().map(y => ({ value: String(y), label: t('dashboard.yearLabel', { year: y }) }))}
              />
            </div>
            <button type="button" onClick={() => window.print()} className={btnSecondary} disabled={!current.data}>
              {t('reports.print')}
            </button>
          </div>
        </div>
      </div>

      {/* 分頁籤（底線式，同 HR 報表） */}
      <nav className="mb-4 flex gap-6 border-b border-line overflow-x-auto scrollbar-hide no-print" aria-label={t('reports.title')}>
        {TABS.map(key => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => setTab(key)}
            className={`-mb-px h-10 whitespace-nowrap border-b-2 text-sm font-medium transition-colors ${
              tab === key ? 'border-accent text-accent' : 'border-transparent text-fg-secondary hover:text-fg'
            }`}
          >
            {t(`reports.tabs.${TAB_KEYS[key]}`)}
          </button>
        ))}
      </nav>

      <div className="report-print">
        {/* 列印時的頁首：公司名稱＋報表名稱＋期間（§3-16）；畫面上只顯示期間 */}
        <div className="mb-3">
          <p className="print-only hidden text-base font-semibold">{user?.organization?.name}</p>
          <p className="print-only hidden text-lg font-semibold">{reportTitle}</p>
          <p className="text-[13px] text-fg-secondary">{periodText}</p>
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

export default ReportsPage;
